import { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, Phone, Mail, ExternalLink } from "lucide-react";
import MediaLink from "@/components/MediaLink";
import SocialLinks from "@/components/SocialLinks";
import AndringsforslagButton from "@/components/AndringsforslagButton";
import EntryFootnote from "@/components/EntryFootnote";
import ExpandableBeskrivning from "@/components/ExpandableBeskrivning";
import GalleriStrip from "@/components/GalleriStrip";
import { usePageMeta } from "@/utils/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OMRADEN = [
  { key: "Handla & vardag", intro: "Det mesta av vardagshandeln löser man i Backe (Fjällsjö) eller Rossön (Bodum) – mataffär, apotek, post och Systembolagets ombud finns på plats. Större kedjor och Systembolagets fulla sortiment finns i Strömsund." },
  { key: "Service & fordon", intro: "Bilverkstad, däck, drivmedel, post och kontanter. Det som behöver lösas när något går sönder eller tar slut." },
  { key: "Vård & trygghet", intro: "Vårdcentral och tandvård finns i Strömsund. Vid akut nöd: ring 112." },
];

export default function PraktisktIBygden() {
  usePageMeta({
    title: "Praktiskt – vidalv.live",
    description: "Handla, service, vård och trygghet i Backe, Rossön och bygden. Praktisk information om vardagen – butiker, verkstad, vårdcentral och kontakter.",
    path: "/praktiskt",
  });
  const [vardagskort, setVardagskort] = useState([]);
  const [tjanstekort, setTjanstekort] = useState([]);

  useEffect(() => {
    axios.get(`${API}/praktiskt/vardagskort`).then(r => setVardagskort(r.data)).catch(() => {});
    axios.get(`${API}/praktiskt/tjanstekort`).then(r => setTjanstekort(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="praktiskt-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <p className="font-body text-xs font-semibold text-bark/50 uppercase tracking-[0.2em] mb-1">Praktiskt</p>
      <h1 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-3">Vardagen i bygden</h1>
      <p className="text-sm text-bark mb-10 max-w-2xl leading-relaxed">
        Det viktigaste att veta om vardagen här. Var handlar man, vem ringer man, hur fungerar saker.
      </p>

      {OMRADEN.map((omrade) => {
        const kort = vardagskort
          .filter(k => k.omrade === omrade.key)
          .sort((a, b) => (a.rubrik || "").localeCompare(b.rubrik || "", "sv", { sensitivity: "base" }));
        return (
          <section key={omrade.key} data-testid={`omrade-${omrade.key.toLowerCase().replace(/\s|&/g, "-")}`} className="mb-12">
            <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2">{omrade.key}</h2>
            <p className="text-sm text-bark mb-6">{omrade.intro}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {kort.map(k => <Vardagskort key={k.id} kort={k} />)}
            </div>
          </section>
        );
      })}

      <section data-testid="omrade-tjanster-foretag" className="mb-12">
        <h2 className="font-fraktur-smooth text-xl sm:text-2xl text-falu tracking-wide mb-2">Tjänster & företag</h2>
        <p className="text-sm text-bark mb-6">Lokala hantverkare, snöröjare och servicetjänster.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[...tjanstekort]
            .sort((a, b) => (a.foretagsnamn || "").localeCompare(b.foretagsnamn || "", "sv", { sensitivity: "base" }))
            .map(k => <Tjanstekort key={k.id} kort={k} />)}
        </div>
        <p className="text-xs text-bark mt-6 leading-relaxed max-w-2xl">
          &ldquo;Fråga om RUT&rdquo; och &ldquo;Fråga om ROT&rdquo; betyder att företaget uppger att de hanterar arbete som kan vara relevant för avdrag. Kontakta företaget och Skatteverket vid frågor.
        </p>
      </section>
    </div>
  );
}

function Vardagskort({ kort }) {
  return (
    <div data-testid={`vardagskort-${kort.id}`} className="bg-parchment border border-warm-border rounded-sm p-5">
      <h3 className="font-heading text-lg font-semibold text-ink mb-1">{kort.rubrik}</h3>
      {kort.ort && <p className="text-xs text-bark flex items-center gap-1 mb-1"><MapPin size={12} /> {kort.ort}</p>}
      <ExpandableBeskrivning kort={kort.kortbeskrivning} full={kort.beskrivning} testId={`vardagskort-${kort.id}-besk`} className="mb-2" />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {kort.kontakt_telefon && <a href={`tel:${kort.kontakt_telefon.replace(/\s/g, "")}`} className="flex items-center gap-1 text-falu hover:text-falu-hover no-underline"><Phone size={12} /> {kort.kontakt_telefon}</a>}
        {kort.kontakt_epost && <a href={`mailto:${kort.kontakt_epost}`} className="flex items-center gap-1 text-falu hover:text-falu-hover no-underline"><Mail size={12} /> {kort.kontakt_epost}</a>}
        {kort.extern_lank && <a href={kort.extern_lank} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-falu hover:text-falu-hover no-underline"><ExternalLink size={12} /> {kort.kalltext || "Länk"}</a>}
      </div>
      {/* Etappe 4.2 — universella sociala länkar (Web/FB/IG/YT). Visas endast om fyllda. */}
      <SocialLinks
        webbplats={kort.webbplats}
        facebook_url={kort.facebook_url}
        instagram_url={kort.instagram_url}
        youtube_url={kort.youtube_url}
      />
      {/* Etappe 4.11 — galleri (visas endast om bilder finns) */}
      {Array.isArray(kort.galleri) && kort.galleri.length > 0 && (
        <div className="mt-3">
          <GalleriStrip bilder={kort.galleri} galleriAudio={kort.galleri_audio} alt={kort.rubrik} />
        </div>
      )}
      <MediaLink media_url={kort.media_url} media_typ={kort.media_typ} media_beskrivning={kort.media_beskrivning} />
      <EntryFootnote
        taggar={kort.taggar}
        kalla={kort.kalla || kort.kalltext}
        senast={kort.senast_andrad || kort.senast_kontrollerad}
        uppdaterad_av={kort.andrad_av}
        testIdPrefix={`vardagskort-${kort.id}`}
      />
      <div className="mt-3 pt-2 border-t border-warm-border/60">
        <AndringsforslagButton typ="vardagskort" referensId={kort.id} referensNamn={kort.rubrik} />
      </div>
    </div>
  );
}

function Tjanstekort({ kort }) {
  return (
    <div data-testid={`tjanstekort-${kort.id}`} className="bg-parchment border border-warm-border rounded-sm p-5">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="text-xs font-semibold px-2 py-0.5 rounded-sm bg-pine/10 text-pine uppercase tracking-wide">Företag</span>
        {kort.fraga_om_rut && <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">Fråga om RUT</span>}
        {kort.fraga_om_rot && <span className="text-xs px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700">Fråga om ROT</span>}
      </div>
      <h3 className="font-heading text-lg font-semibold text-ink mb-1">{kort.foretagsnamn}</h3>
      {kort.ort && <p className="text-xs text-bark flex items-center gap-1 mb-1"><MapPin size={12} /> {kort.ort}</p>}
      <ExpandableBeskrivning kort={kort.kortbeskrivning} full={kort.beskrivning} testId={`tjanstekort-${kort.id}-besk`} className="mb-2" />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        {kort.kontakt_telefon && <a href={`tel:${kort.kontakt_telefon.replace(/\s/g, "")}`} className="flex items-center gap-1 text-falu hover:text-falu-hover no-underline"><Phone size={12} /> {kort.kontakt_telefon}</a>}
        {kort.kontakt_epost && <a href={`mailto:${kort.kontakt_epost}`} className="flex items-center gap-1 text-falu hover:text-falu-hover no-underline"><Mail size={12} /> {kort.kontakt_epost}</a>}
      </div>
      {/* Etappe 4.1/4.2 — universella sociala länkar */}
      <SocialLinks
        webbplats={kort.webbplats}
        facebook_url={kort.facebook_url}
        instagram_url={kort.instagram_url}
        youtube_url={kort.youtube_url}
      />
      {/* Etappe 4.11 — galleri (visas endast om bilder finns) */}
      {Array.isArray(kort.galleri) && kort.galleri.length > 0 && (
        <div className="mt-3">
          <GalleriStrip bilder={kort.galleri} galleriAudio={kort.galleri_audio} alt={kort.foretagsnamn} />
        </div>
      )}
      <MediaLink media_url={kort.media_url} media_typ={kort.media_typ} media_beskrivning={kort.media_beskrivning} />
      <EntryFootnote
        taggar={kort.taggar}
        kalla={kort.kalla || kort.kalltext}
        senast={kort.senast_andrad || kort.senast_kontrollerad}
        uppdaterad_av={kort.andrad_av}
        testIdPrefix={`tjanstekort-${kort.id}`}
      />
      <div className="mt-3 pt-2 border-t border-warm-border/60">
        <AndringsforslagButton typ="foretag" referensId={kort.id} referensNamn={kort.foretagsnamn} />
      </div>
    </div>
  );
}
